/**
 * 绑定音色弹窗
 * v10.2 新增：选择已发布的音色卡片绑定到场景人格
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group'
import { Label } from '@/shared/components/ui/label'
import { Link2, Loader2, Mic, AlertCircle } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService } from '@/modules/identity/services'
import type { Voice } from '@/modules/identity/types'

interface VoiceBindDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  identityId: string
  sceneId: string
  sceneName: string
  currentVoiceId?: string | null
  onBindSuccess?: () => void
}

export function VoiceBindDialog({
  open,
  onOpenChange,
  identityId,
  sceneId,
  sceneName,
  currentVoiceId,
  onBindSuccess,
}: VoiceBindDialogProps) {
  const { showSuccess, showError } = useToast()

  const [voices, setVoices] = useState<Voice[]>([])
  const [loadingVoices, setLoadingVoices] = useState(false)
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('')
  const [binding, setBinding] = useState(false)

  // 加载已发布的音色列表
  const loadVoices = useCallback(async () => {
    setLoadingVoices(true)
    try {
      const response = await IdentityApiService.listVoices(identityId)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const allVoices = (response.data as any)?.data || response.data || []
      // 只显示已发布的音色
      const publishedVoices = allVoices.filter((v: Voice) => v.status === 'published')
      setVoices(publishedVoices)
    } catch (error) {
      console.error('Failed to load voices:', error)
      setVoices([])
    } finally {
      setLoadingVoices(false)
    }
  }, [identityId])

  // 弹窗打开时加载音色列表
  useEffect(() => {
    if (open) {
      loadVoices()
      setSelectedVoiceId(currentVoiceId || '')
    }
  }, [open, loadVoices, currentVoiceId])

  // 确认绑定
  const handleBind = useCallback(async () => {
    if (!selectedVoiceId) {
      showError('请选择一个音色', '验证失败')
      return
    }

    setBinding(true)
    try {
      await IdentityApiService.bindVoiceToFacet(identityId, sceneId, selectedVoiceId)
      showSuccess(`已绑定音色到「${sceneName}」`, '绑定成功')
      onOpenChange(false)
      onBindSuccess?.()
    } catch (error) {
      console.error('Failed to bind voice:', error)
      showError('绑定失败', '操作失败')
    } finally {
      setBinding(false)
    }
  }, [identityId, sceneId, sceneName, selectedVoiceId, showSuccess, showError, onOpenChange, onBindSuccess])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-violet-500" />
            绑定音色
          </DialogTitle>
          <DialogDescription>
            为场景「{sceneName}」选择一个已发布的音色卡片
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loadingVoices ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
              <span className="ml-2 text-sm text-slate-500">加载音色列表...</span>
            </div>
          ) : voices.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-amber-700 font-medium">暂无已发布的音色</p>
                  <p className="text-xs text-amber-600 mt-1">
                    请先在「声音克隆」Tab 完成音色训练并发布后再进行绑定。
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <RadioGroup
              value={selectedVoiceId}
              onValueChange={setSelectedVoiceId}
              className="space-y-3"
            >
              {voices.map((voice) => (
                <div
                  key={voice.id}
                  className={cn(
                    'flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all',
                    selectedVoiceId === voice.id
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-slate-200 hover:border-slate-300'
                  )}
                  onClick={() => setSelectedVoiceId(voice.id)}
                >
                  <RadioGroupItem value={voice.id} id={voice.id} />
                  <Label
                    htmlFor={voice.id}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Mic className="h-4 w-4 text-slate-400" />
                      <span className="font-medium">{voice.name}</span>
                      {voice.is_default && (
                        <Badge variant="secondary" className="text-xs">默认</Badge>
                      )}
                      {currentVoiceId === voice.id && (
                        <Badge variant="secondary" className="text-xs bg-violet-100 text-violet-700">当前</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      语速 {voice.speech_rate?.toFixed(1) || '1.0'} | 
                      音调 {voice.pitch_rate?.toFixed(1) || '1.0'} | 
                      音量 {voice.volume || 50}
                    </p>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}

          <p className="text-xs text-slate-400 mt-4">
            ⚠️ 只显示已发布的音色卡片。绑定后，该场景在 Agent Flow 中运行时将使用此音色。
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={binding}
          >
            取消
          </Button>
          <Button
            onClick={handleBind}
            disabled={!selectedVoiceId || binding || voices.length === 0}
          >
            {binding ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                绑定中...
              </>
            ) : (
              '确认绑定'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
